import { toReactive, useCurrentElement, type MaybeElementRef, unrefElement } from '@vueuse/core';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import chroma from 'chroma-js';
import { useThemePreference } from '@/modules/current-user/composables';

/**
 * This function allows us to merge tailwind classes in an overrideable way:
 * e.g., a component can have `class="font-bold"` and that can be overriden by
 * passing `class="font-normal"` on consumption.
 *
 * See BaseHeading.vue for an example usage.
 *
 * This should likely only be used on generic components, not everywhere: while
 * this supports all the functionality of `clsx`, we don't usually need that in
 * Vue as it's got it all built in, except for the tailwind merging.
 */
export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

/**
 * A simplified, opinionated fork of VueUse's useCssVar, which doesn't mutate
 * CSS variables and cause strange bugs like the VueUse one does.
 */
export function useCssVar(prop: string, target?: MaybeElementRef) {
  const elRef = computed(() => unrefElement(target) || document.body);
  const getValue = () => window.getComputedStyle(elRef.value).getPropertyValue(prop)?.trim();

  const variable = shallowRef(getValue());

  function updateCssVar() {
    variable.value = getValue();
  }
  watchEffect(updateCssVar);

  // This replaces the observe functionality from the original composable
  const { theme } = useThemePreference();
  watch(theme, updateCssVar);

  return variable;
}

/**
 * Helper function for using CSS variables defined in our tailwind config.
 *
 * Returns multiple reactive objects so that if someone tries to destructure
 * the return value, it won't break reactivity
 */
export function useTheme() {
  // The target intentionally isn't configurable and if you're trying to
  // configure it, you're probably trying to do something that you shouldn't be
  const currentEl = useCurrentElement();

  function getScaleForData(colors: string[]) {
    const scale = chroma.scale(colors).mode('lrgb');
    return (num: number) => scale(num).hex();
  }

  const neutral50 = useCssVar('--color-neutral-50', currentEl);
  const neutral200 = useCssVar('--color-neutral-200', currentEl);
  const neutral300 = useCssVar('--color-neutral-300', currentEl);
  const neutral400 = useCssVar('--color-neutral-400', currentEl);
  const neutral500 = useCssVar('--color-neutral-500', currentEl);
  const neutral600 = useCssVar('--color-neutral-600', currentEl);
  const neutral700 = useCssVar('--color-neutral-700', currentEl);
  const neutral800 = useCssVar('--color-neutral-800', currentEl);
  const neutral950 = useCssVar('--color-neutral-950', currentEl);

  const blue300 = useCssVar('--color-blue-300', currentEl);
  const blue500 = useCssVar('--color-blue-500', currentEl);
  const blue700 = useCssVar('--color-blue-700', currentEl);

  const mint300 = useCssVar('--color-mint-300', currentEl);
  const mint400 = useCssVar('--color-mint-400', currentEl);
  const mint500 = useCssVar('--color-mint-500', currentEl);
  const mint600 = useCssVar('--color-mint-600', currentEl);

  const purple300 = useCssVar('--color-purple-300', currentEl);
  const purple500 = useCssVar('--color-purple-500', currentEl);
  const purple700 = useCssVar('--color-purple-700', currentEl);
  const purple800 = useCssVar('--color-purple-800', currentEl);

  const colors = toReactive(
    computed(() => ({
      neutral50: neutral50.value,
      neutral200: neutral200.value,
      neutral300: neutral300.value,
      neutral400: neutral400.value,
      neutral500: neutral500.value,
      neutral600: neutral600.value,
      neutral700: neutral700.value,
      neutral800: neutral800.value,
      neutral950: neutral950.value,
      blue300: blue300.value,
      blue500: blue500.value,
      blue700: blue700.value,
      mint300: mint300.value,
      mint400: mint400.value,
      mint500: mint500.value,
      mint600: mint600.value,
      purple300: purple300.value,
      purple500: purple500.value,
      purple700: purple700.value,
      purple800: purple800.value,
    })),
  );

  const accentPrimary = useCssVar('--accent-color-primary', currentEl);
  const accentPrimaryMuted = useCssVar('--accent-color-primary-muted', currentEl);
  const accentDataHighest = useCssVar('--accent-color-data-highest', currentEl);
  const accentDataHighestMuted = useCssVar('--accent-color-data-highest-muted', currentEl);
  const accentDataHigh = useCssVar('--accent-color-data-high', currentEl);
  const accentDataHighMuted = useCssVar('--accent-color-data-high-muted', currentEl);
  const accentDataMedium = useCssVar('--accent-color-data-medium', currentEl);
  const accentDataMediumMuted = useCssVar('--accent-color-data-medium-muted', currentEl);
  const accentDataLow = useCssVar('--accent-color-data-low', currentEl);
  const accentDataLowMuted = useCssVar('--accent-color-data-low-muted', currentEl);
  const accentDataLowest = useCssVar('--accent-color-data-lowest', currentEl);
  const accentDataLowestMuted = useCssVar('--accent-color-data-lowest-muted', currentEl);

  const accent = toReactive(
    computed(() => ({
      primary: accentPrimary.value,
      primaryMuted: accentPrimaryMuted.value,
      dataScale: getScaleForData([
        accentDataHighest.value,
        accentDataHigh.value,
        accentDataMedium.value,
        accentDataLow.value,
        accentDataLowest.value,
      ]),
      dataScaleMuted: getScaleForData([
        accentDataHighestMuted.value,
        accentDataHighMuted.value,
        accentDataMediumMuted.value,
        accentDataLowMuted.value,
        accentDataLowestMuted.value,
      ]),
    })),
  );

  const borderNeutral = useCssVar('--border-color-neutral', currentEl);
  const borderNeutralMuted = useCssVar('--border-color-neutral-muted', currentEl);
  const borderDataHighest = useCssVar('--border-color-data-highest', currentEl);
  const borderDataHighestMuted = useCssVar('--border-color-data-highest-muted', currentEl);
  const borderDataHigh = useCssVar('--border-color-data-high', currentEl);
  const borderDataHighMuted = useCssVar('--border-color-data-high-muted', currentEl);
  const borderDataMedium = useCssVar('--border-color-data-medium', currentEl);
  const borderDataMediumMuted = useCssVar('--border-color-data-medium-muted', currentEl);
  const borderDataLow = useCssVar('--border-color-data-low', currentEl);
  const borderDataLowMuted = useCssVar('--border-color-data-low-muted', currentEl);
  const borderDataLowest = useCssVar('--border-color-data-lowest', currentEl);
  const borderDataLowestMuted = useCssVar('--border-color-data-lowest-muted', currentEl);

  const border = toReactive(
    computed(() => ({
      neutral: borderNeutral.value,
      neutralMuted: borderNeutralMuted.value,
      dataScale: getScaleForData([
        borderDataHighest.value,
        borderDataHigh.value,
        borderDataMedium.value,
        borderDataLow.value,
        borderDataLowest.value,
      ]),
      dataScaleMuted: getScaleForData([
        borderDataHighestMuted.value,
        borderDataHighMuted.value,
        borderDataMediumMuted.value,
        borderDataLowMuted.value,
        borderDataLowestMuted.value,
      ]),
    })),
  );

  const bgDefault = useCssVar('--background-color-default', currentEl);
  const bgNeutral = useCssVar('--background-color-neutral', currentEl);
  const bgNeutralActive = useCssVar('--background-color-neutral-active', currentEl);
  const bgStrongNeutral = useCssVar('--background-color-strong-neutral', currentEl);
  const bgInfo = useCssVar('--background-color-info', currentEl);
  const bgStrongDataHighest = useCssVar('--background-color-strong-data-highest', currentEl);
  const bgStrongDataHigh = useCssVar('--background-color-strong-data-high', currentEl);
  const bgStrongDataMedium = useCssVar('--background-color-strong-data-medium', currentEl);
  const bgStrongDataLow = useCssVar('--background-color-strong-data-low', currentEl);
  const bgStrongDataLowest = useCssVar('--background-color-strong-data-lowest', currentEl);

  const bg = toReactive(
    computed(() => ({
      default: bgDefault.value,
      neutral: bgNeutral.value,
      neutralActive: bgNeutralActive.value,
      strongNeutral: bgStrongNeutral.value,
      info: bgInfo.value,
      strongDataScale: getScaleForData([
        bgStrongDataHighest.value,
        bgStrongDataHigh.value,
        bgStrongDataMedium.value,
        bgStrongDataLow.value,
        bgStrongDataLowest.value,
      ]),
    })),
  );

  const textNeutral = useCssVar('--text-color-neutral', currentEl);
  const textNeutralMuted = useCssVar('--text-color-neutral-muted', currentEl);

  const text = toReactive(
    computed(() => ({
      neutral: textNeutral.value,
      neutralMuted: textNeutralMuted.value,
    })),
  );

  const fontSans = useCssVar('--font-sans', currentEl);
  const fontMono = useCssVar('--font-mono', currentEl);

  const font = toReactive(
    computed(() => ({
      sans: fontSans.value,
      mono: fontMono.value,
    })),
  );

  // Using this is an anti-pattern and should be avoided wherever possible in
  // favour of the colours above. This is used as an escape hatch where we want
  // to e.g. pass the user's colour preference to an external service
  const isDarkDangerous = computed(() => {
    if (!(currentEl.value instanceof HTMLElement)) {
      return false;
    }

    return !!currentEl.value.closest('.tw-dark');
  });

  return { colors, accent, border, bg, text, font, isDarkDangerous };
}
